import classNames from 'classnames';
import _ from 'lodash';
import type { AttributeMap, Op } from 'quill/core';
import { Delta } from 'quill/core';
import type { ReactElement, RefCallback, RefObject } from 'react';
import React, {
  cloneElement,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import type { EmbedValue } from '../editor/embed/matchEmbed';
import { EmbedMode, embedUrl } from '../editor/embed/matchEmbed';
import type { CardValue } from '../editor/formats/Card';
import MentionBase from '../editor/mentions/MentionBase';
import UserMentionIcon from '../editor/mentions/UserMentionIcon';
import type { TableChange } from '../editor/table/types';
import deltaToRenderBlocks from '../editor/utils/deltaToRenderBlocks';
import getFirstImageUrl from '../editor/utils/getFirstImageUrl';
import tableIcon from '../icons/non-prefilled/16x16/table.svg';
import postIcon from '../icons/prefilled/16x16/post.svg';
import topicIcon from '../icons/prefilled/16x16/topic.svg';
import getExternalBrandLogoProps from '../integrations/getExternalBrandLogoProps';
import fullOrgUrl from '../utils/url/fullOrgUrl';
import includesExtension from '../utils/url/includesExtension';
import postPath from '../utils/url/postPath';
import * as styles from './DeltaPreview.module.scss';
import DeltaPreviewParagraph from './DeltaPreviewParagraph';
import ExternalBrandLogo from './ExternalBrandLogo';
import SVGIcon from './SVGIcon';

interface DeltaPreviewPillProps {
  children: React.ReactNode;
  icon: ReactElement;
}

const DeltaPreviewPill = React.forwardRef<
  HTMLDivElement,
  DeltaPreviewPillProps
>(({ children, icon }, ref) => (
  <div ref={ref} className={styles.pill}>
    <div className={styles.pillIcon} aria-label="icon">
      {icon}
    </div>
    <div className={styles.pillContent}>{children}</div>
  </div>
));

type GetMentionIconArgs = {
  type: string;
  id: string;
};

export const getMentionIcon = ({ type, id }: GetMentionIconArgs) => {
  switch (type) {
    case 'post':
      return postIcon;
    case 'topic':
      return topicIcon;
    case 'user': {
      return <UserMentionIcon id={id} />;
    }
    case 'external-post':
    case 'group':
      return null;
    default:
      return <ExternalBrandLogo inline {...getExternalBrandLogoProps(type)} />;
  }
};

type ElementRendererProps = {
  maxLines?: number;
};

type ElementRenderer = (props: ElementRendererProps) => ReactElement;

type BlockEmbedHandler = (
  value: unknown,
  key: string,
  attributes: AttributeMap,
) => ReactElement | ElementRenderer | null;

type BlockEmbedHandlerMap = {
  [key: string]: BlockEmbedHandler;
};

const blockEmbedHandlers: BlockEmbedHandlerMap = {
  image: (value, key) => {
    const src = getFirstImageUrl(value);
    return src ? (
      <figure key={key} className={classNames(styles.image, 'preview-image')}>
        <img src={src} alt="" />
      </figure>
    ) : null;
  },
  video: (value, key) =>
    typeof value === 'string' ? (
      <div key={key} className={styles.video}>
        {includesExtension(value, ['mp4', 'mov']) ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video src={value} aria-label="Video" />
        ) : (
          // eslint-disable-next-line jsx-a11y/iframe-has-title
          <iframe className={styles.video} src={value} frameBorder="0" />
        )}
      </div>
    ) : null,
  'table-embed': (value, key) => {
    if (value && typeof value === 'object') {
      const tableEmbed = value as TableChange;
      const cellEntries = Object.entries(tableEmbed.cells ?? {});

      const firstCellEntry = cellEntries[0];
      if (firstCellEntry == null) return null;

      const [firstCell, { content }] = firstCellEntry;

      const hasEllipsisAfter = cellEntries.length > 1;

      return (
        <DeltaPreviewPill
          icon={<SVGIcon size={12}>{tableIcon}</SVGIcon>}
          key={key}
        >
          <span>{firstCell !== '1:1' && '…'}</span>
          <span
            className={classNames(styles.tableEmbedPillContent, {
              [styles.hasEllipsisAfter]: hasEllipsisAfter,
            })}
          >
            <DeltaPreviewParagraph
              content={content ?? []}
              link={false}
              showHoverCard={false}
            />
          </span>
          <span>{hasEllipsisAfter && '…'}</span>
        </DeltaPreviewPill>
      );
    }
    return null;
  },
  'code-embed': (value, key) => {
    if (value && Array.isArray(value)) {
      const content = value as Op[];

      return ({ maxLines }) => (
        <pre key={key}>
          <DeltaPreview
            content={content}
            typography="code"
            maxLines={maxLines}
          />
        </pre>
      );
    }
    return null;
  },
  embed: (value, key) => {
    if (value && typeof value === 'object') {
      const embed = value as EmbedValue;
      const url = embedUrl(embed, EmbedMode.FULL);
      if (url) {
        return (
          <div key={key}>
            <MentionBase
              type={embed.type}
              icon={getMentionIcon(embed)}
              content={url}
            />
          </div>
        );
      }
    }
    return null;
  },
  card: (value, key) => {
    if (value && typeof value === 'object') {
      const card = value as CardValue;
      const url = fullOrgUrl(postPath({ id: card.id, title: '' }));
      return (
        <div key={key}>
          <MentionBase
            type={card.type}
            icon={getMentionIcon(card)}
            content={url}
          />
        </div>
      );
    }
    return null;
  },
};

type ElementProps = {
  className?: string;
  style?: React.CSSProperties;
  ref?: RefCallback<HTMLElement>;
};

type BlockDef = {
  render: ElementRenderer;
  elementProps?: ElementProps;
  hasSearchHighlight: boolean;
} & ({ isEmbed: true; linesCount: number } | { isEmbed: false });

type RenderedBlockDef = Exclude<BlockDef, 'render'> & {
  element: ReturnType<ElementRenderer>;
};

const formatMapper: {
  test: (attributes: AttributeMap) => boolean;
  map: (children: ReactElement, attributes: AttributeMap) => ReactElement;
}[] = [
  {
    test: (attributes) => typeof attributes.header === 'number',
    map: (children, attributes) => {
      const HTag = `h${Math.min(attributes.header as number, 3)}` as
        | 'h1'
        | 'h2'
        | 'h3';
      return <HTag>{children}</HTag>;
    },
  },
  {
    test: (attributes) => !!attributes.blockquote,
    map: (children) => <div className="ql-blockquote-block">{children}</div>,
  },
  {
    test: (attributes) => !!attributes.list,
    map: (children, attributes) => (
      <li
        data-list={attributes.list}
        className={
          typeof attributes.indent === 'number' && attributes.indent > 0
            ? `ql-indent-${attributes.indent}`
            : undefined
        }
      >
        <span className="ql-ui" contentEditable={false} />
        {children}
      </li>
    ),
  },
  {
    test: () => true,
    map: (element) => <div className="editor-view-paragraph">{element}</div>,
  },
];

const getBlockEmbedLines = (key: string, value: unknown): number => {
  if (
    key === 'code-embed' &&
    (Array.isArray(value) || value instanceof Delta)
  ) {
    return deltaToRenderBlocks(new Delta(value)).length;
  }

  return 1;
};

const convertContentToBlockDefs = ({
  content,
  link,
  showHoverCard,
  additionalBlockEmbedHandlers,
}: {
  content: Op[] | Delta;
  link: boolean;
  showHoverCard: boolean;
  additionalBlockEmbedHandlers: BlockEmbedHandlerMap;
}) => {
  const blockDefs: BlockDef[] = [];

  deltaToRenderBlocks(new Delta(content)).forEach((renderBlock, index) => {
    const renderKey = `${index}`;

    if (renderBlock.type === 'embed') {
      const { key, value, attributes } = renderBlock;
      const linesCount = getBlockEmbedLines(key, value);
      const handler =
        additionalBlockEmbedHandlers[key] || blockEmbedHandlers[key];

      const elementOrRenderFn = handler?.(value, renderKey, attributes);

      if (elementOrRenderFn) {
        const render =
          typeof elementOrRenderFn === 'function'
            ? elementOrRenderFn
            : () => elementOrRenderFn;

        blockDefs.push({
          render,
          isEmbed: true,
          hasSearchHighlight: false,
          linesCount,
        });
      }

      return;
    }

    const children = (
      <DeltaPreviewParagraph
        content={renderBlock.line.ops}
        link={link}
        showHoverCard={showHoverCard}
      />
    );

    const { attributes } = renderBlock;

    formatMapper.some(({ test, map }) => {
      if (test(attributes)) {
        blockDefs.push({
          render: ({ maxLines }) => {
            return cloneElement(map(children, attributes), {
              key: renderKey,
              style:
                maxLines != null
                  ? {
                      display: '-webkit-box',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: maxLines,
                    }
                  : undefined,
            });
          },
          isEmbed: false,
          hasSearchHighlight: renderBlock.line.ops.some(
            (op) => op.attributes?.searchHighlight,
          ),
        });
        return true;
      }
      return false;
    });
  });

  return blockDefs;
};

type Boundary = {
  slice: [start: number, end: number];
  lastBlockMaxLines: number;
};

const calculateBlockBoundary = (
  items: { blockDef: BlockDef; domNode: HTMLElement }[],
  maxLines: number,
): Boundary | null => {
  const centerIndex = Math.max(
    items.findIndex(({ blockDef }) => blockDef.hasSearchHighlight),
    0,
  );

  const memoizedGetLineCount = _.memoize(
    ({ blockDef, domNode }: { blockDef: BlockDef; domNode: HTMLElement }) => {
      if (!blockDef.isEmbed) {
        const style = getComputedStyle(domNode);
        const contentHeight =
          domNode.clientHeight -
          parseFloat(style.paddingTop) -
          parseFloat(style.paddingBottom);

        const result = Math.round(contentHeight / parseFloat(style.lineHeight));
        if (!Number.isNaN(result)) {
          return result;
        }

        return 1;
      }
      return blockDef.linesCount;
    },
  );

  const center = items[centerIndex];
  if (!center) return null;
  const centerTaken = memoizedGetLineCount(center);
  const aboveTakens: number[] = [];
  const belowTakens: number[] = [];

  let takenLines = centerTaken;
  while (takenLines < maxLines) {
    const nextAbove = items[centerIndex - 1 - aboveTakens.length];
    const nextBelow = items[centerIndex + 1 + belowTakens.length];
    if (!nextAbove && !nextBelow) break;

    const shouldPickFromBelow =
      !nextAbove ||
      (nextBelow && _.sum(belowTakens) <= _.sum(aboveTakens)) ||
      // It doesn't make sense to apply line-clamp to prior elements
      // so we don't pick from above if the remaining lines are not enough.
      memoizedGetLineCount(nextAbove) + takenLines > maxLines;

    if (shouldPickFromBelow && nextBelow) {
      const lineCount = memoizedGetLineCount(nextBelow);
      belowTakens.push(lineCount);
      takenLines += lineCount;
    } else if (!shouldPickFromBelow && nextAbove) {
      const lineCount = memoizedGetLineCount(nextAbove);
      aboveTakens.push(lineCount);
      takenLines += lineCount;
    } else {
      break;
    }
  }

  const lastBlockLineCount = _.last(belowTakens) || centerTaken;
  const isClamped =
    takenLines > maxLines ||
    aboveTakens.length + 1 + belowTakens.length < items.length;
  if (!isClamped) return null;

  const end = centerIndex + 1 + belowTakens.length;
  return {
    slice: [centerIndex - aboveTakens.length, end],
    lastBlockMaxLines: Math.max(lastBlockLineCount + maxLines - takenLines, 1),
  };
};

type OnCanBeClampedChange = (canBeClamped: boolean) => void;
type InnerDeltaPreviewProps = {
  typography:
    | 'comment'
    | 'card'
    | 'searchResult'
    | 'publicSearchResult'
    | 'code'
    | 'cell'
    | 'searchBar'
    | 'activity';
  content: Delta | Op[];
  link?: boolean;
  showHoverCard?: boolean;
  additionalBlockEmbedHandlers?: BlockEmbedHandlerMap;
} & (
  | {
      maxLines: number;
      disableLineClamp?: boolean;
      onCanBeClampedChange?: OnCanBeClampedChange;
    }
  | {
      maxLines?: never;
      disableLineClamp?: never;
      onCanBeClampedChange?: never;
    }
);

const useClampedBlocks = (
  blockDefs: BlockDef[],
  maxLines: number | undefined,
  disableLineClamp: boolean,
  onCanBeClampedChangeRef: RefObject<OnCanBeClampedChange>,
  containerRef: RefObject<HTMLDivElement>,
) => {
  const [boundary, setBoundary] = useState<Boundary | null>(null);
  const boundaryAppliedBlockDefs: RenderedBlockDef[] =
    boundary && !disableLineClamp
      ? applyBoundary(blockDefs, boundary)
      : blockDefs.map((blockDef) => ({
          ...blockDef,
          element: blockDef.render({}),
        }));

  const domNodesMapRef = useRef<Map<BlockDef, HTMLElement> | null>(null);

  const getDOMNodesMap = () => {
    if (!domNodesMapRef.current) {
      domNodesMapRef.current = new Map();
    }
    return domNodesMapRef.current;
  };

  const withRefCallbacks = boundaryAppliedBlockDefs.map((blockDef) => {
    const blockWithRefFiller: RenderedBlockDef = {
      ...blockDef,
      element: cloneElement(blockDef.element, {
        ...blockDef.elementProps,
        ref: (domNode: HTMLElement) => {
          const map = getDOMNodesMap();
          if (domNode) {
            map.set(blockWithRefFiller, domNode);
          } else {
            map.delete(blockWithRefFiller);
          }
        },
      }),
    };
    return blockWithRefFiller;
  });

  const blockDefsWithDOMNodesRef = useRef<
    { blockDef: BlockDef; domNode: HTMLElement }[]
  >([]);

  useLayoutEffect(() => {
    blockDefsWithDOMNodesRef.current = withRefCallbacks.flatMap((blockDef) => {
      const domNode = getDOMNodesMap().get(blockDef);
      return domNode ? [{ blockDef, domNode }] : [];
    });
  }, [withRefCallbacks]);

  useLayoutEffect(() => {
    if (typeof maxLines !== 'number' || !containerRef.current) return undefined;

    const updateBoundary = () => {
      const newBoundary = calculateBlockBoundary(
        blockDefsWithDOMNodesRef.current,
        maxLines,
      );

      setBoundary(newBoundary);
      onCanBeClampedChangeRef.current?.(!!newBoundary);
    };

    const isContainerInitiallyVisible = !!containerRef.current.offsetParent;
    if (isContainerInitiallyVisible) {
      updateBoundary();
    }

    let lastWidth: number | null = null;
    const observer = new ResizeObserver((entries) => {
      const { width } = entries[0]?.contentRect ?? { width: null };
      if (width === lastWidth) return;

      const isElementVisible = typeof width === 'number' && width > 0;
      const isInitialCall = lastWidth === null;
      if (
        isElementVisible &&
        // Resize observer triggers an initial call when the element being observed is visible.
        (!isInitialCall || !isContainerInitiallyVisible)
      ) {
        // `flushSync` is used because we need to make sure that updates to the boundary
        // happen synchronously so users won't see layout flickers when resizing.
        // Without `flushSync`, React may (in React 18) or may not (in React 17) batch the updates,
        // which is an implementation detail that we don't want to rely on.
        flushSync(() => {
          setBoundary(null);
        });
        flushSync(() => {
          updateBoundary();
        });
      }
      lastWidth = width;
    });

    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
    };
  }, [
    maxLines,
    blockDefsWithDOMNodesRef,
    onCanBeClampedChangeRef,
    containerRef,
  ]);

  return withRefCallbacks.map((blockDef) => blockDef.element);
};

const applyBoundary = (
  blockDefs: BlockDef[],
  boundary: Boundary,
): RenderedBlockDef[] => {
  const sliced = blockDefs.slice(...boundary.slice);

  return sliced.map((blockDef, index) => {
    const isLast = index === sliced.length - 1;
    return {
      ...blockDef,
      element: blockDef.render({
        maxLines: isLast ? boundary.lastBlockMaxLines : undefined,
      }),
    };
  });
};

interface ElementGroupingRule {
  child: { type: keyof HTMLElementTagNameMap; props?: Record<string, unknown> };
  requiredContainer: { type: keyof HTMLElementTagNameMap };
}

const elementGroupingRules: ElementGroupingRule[] = [
  { child: { type: 'li' }, requiredContainer: { type: 'ol' } },
  {
    child: { type: 'div', props: { className: 'ql-blockquote-block' } },
    requiredContainer: { type: 'blockquote' },
  },
];

const InnerDeltaPreview = ({
  typography,
  content,
  link = true,
  showHoverCard = false,
  maxLines,
  disableLineClamp = false,
  onCanBeClampedChange,
  additionalBlockEmbedHandlers,
}: InnerDeltaPreviewProps) => {
  const additionalBlockEmbedHandlersRef = useRef<BlockEmbedHandlerMap>(
    additionalBlockEmbedHandlers ?? {},
  );
  useLayoutEffect(() => {
    additionalBlockEmbedHandlersRef.current =
      additionalBlockEmbedHandlers ?? {};
  }, [additionalBlockEmbedHandlers]);

  const onCanBeClampedChangeRef = useRef(onCanBeClampedChange ?? null);
  useLayoutEffect(() => {
    onCanBeClampedChangeRef.current = onCanBeClampedChange ?? null;
  }, [onCanBeClampedChange]);

  const blockDefs = useMemo(
    () =>
      convertContentToBlockDefs({
        content,
        link,
        showHoverCard,
        additionalBlockEmbedHandlers: additionalBlockEmbedHandlersRef.current,
      }),
    [content, link, showHoverCard],
  );

  const slicedBlockDefs = useMemo(() => {
    if (typeof maxLines !== 'number' || disableLineClamp) return blockDefs;
    const centerIndex = Math.max(
      blockDefs.findIndex(({ hasSearchHighlight }) => hasSearchHighlight),
      0,
    );
    const start = Math.max(centerIndex - maxLines - 1, 0);
    const end = Math.min(centerIndex + maxLines + 1, blockDefs.length - 1);
    return blockDefs.slice(start, end + 1);
  }, [blockDefs, maxLines, disableLineClamp]);

  const containerRef = useRef<HTMLDivElement>(null);
  const blocks = useClampedBlocks(
    slicedBlockDefs,
    maxLines,
    disableLineClamp,
    onCanBeClampedChangeRef,
    containerRef,
  );

  return (
    <div
      ref={containerRef}
      className={classNames(styles.editor, styles[typography], 'ql-editor')}
    >
      {blocks.reduce((acc: ReactElement[], element: ReactElement) => {
        const matchedRule = elementGroupingRules.find(({ child }) =>
          _.isMatch(element, child),
        );
        if (!matchedRule) return [...acc, element];

        const last = _.last(acc);
        if (last && _.isMatch(last, matchedRule.requiredContainer)) {
          return [
            ..._.dropRight(acc),
            cloneElement(
              last,
              last.props,
              <>
                {last.props.children}
                {element}
              </>,
            ),
          ];
        }

        const Tag = matchedRule.requiredContainer.type;
        return [...acc, <Tag key={element.key}>{element}</Tag>];
      }, [])}
    </div>
  );
};

type DeltaPreviewProps = InnerDeltaPreviewProps;

const DeltaPreview = (props: DeltaPreviewProps) => {
  const { ops } = new Delta(props.content);

  const [forceRender, setForceRender] = useState(0);
  const [latestOps, setLatestOps] = useState<Op[]>(ops);

  if (!_.isEqual(latestOps, ops)) {
    setLatestOps(ops);
    setForceRender(forceRender + 1);
    return null;
  }

  return (
    <InnerDeltaPreview
      // Recreate the component when content or maxLines changes,
      // so that the component can be re-rendered with the correct line clamp.
      key={`${forceRender}-${props.maxLines ?? ''}`}
      {...props}
      content={latestOps}
    />
  );
};

DeltaPreview.Pill = DeltaPreviewPill;

export default DeltaPreview;
